import type { Context, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { GlobalApiPaymentDetails } from '@data/global-api-payments';

import { useCancelPayment } from '../../hooks/useCancelPayment/useCancelPayment';
import { useGetPaymentDetails } from '../../hooks/useGetPaymentDetails/useGetPaymentDetails';
import { usePollPaymentDetails } from '../../hooks/usePollPaymentDetails/usePollPaymentDetails';
import { useUpdatePaymentDetails } from '../../hooks/useUpdatePaymentDetails/useUpdatePaymentDetails';
import type { PatchPaymentDetailsBody } from '../../hooks/useUpdatePaymentDetails/useUpdatePaymentDetails.types';

import { SCA_PAYMENT_DETAILS_CONTEXT_DEFAULT } from './ScaPaymentDetailsContext.shared-mocks';
import type {
  GlobalApiPaymentDetailsContextProviderProps,
  ScaApiPaymentDetailsContextData,
} from './ScaPaymentDetailsContext.types';
import { shouldPollPaymentDetails } from './ScaPaymentDetailsContext.utils';

const ScaPaymentDetailsContext =
  createContext<ScaApiPaymentDetailsContextData | null>(null);

export const useScaPaymentDetailsContext = () => {
  const context = useContext(ScaPaymentDetailsContext);

  if (!context) {
    throw new Error(
      'useScaPaymentDetailsContext hook must be used within a <ScaPaymentDetailsContextProvider>'
    );
  }

  return context;
};

export const ScaPaymentDetailsContextProvider = ({
  skip,
  authToken,
  paymentId,
  children,
}: GlobalApiPaymentDetailsContextProviderProps) => {
  const [paymentDetails, setPaymentDetails] =
    useState<GlobalApiPaymentDetails | null>(null);

  const {
    error: getError,
    isError: isGetError,
    isLoading: isGetLoading,
    result: getPaymentDetailsResult,
    execute: refetchPaymentDetails,
  } = useGetPaymentDetails({ paymentId, authToken, skip });

  const [
    patchPaymentDetails,
    { isError: isPatchError, error: patchError, isFetching: isPatchFetching },
  ] = useUpdatePaymentDetails({ paymentId: paymentId, authToken: authToken });

  const [
    postCancelPayment,
    {
      isError: isCancelError,
      error: cancelError,
      isFetching: isCancelFetching,
    },
  ] = useCancelPayment({ paymentId, authToken });

  const { isError: isPollingError, error: pollingError } =
    usePollPaymentDetails({
      paymentId,
      authToken,
      skip: !shouldPollPaymentDetails(paymentDetails),
      handleResponse: setPaymentDetails,
    });

  useEffect(() => {
    if (getPaymentDetailsResult) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPaymentDetails(getPaymentDetailsResult);
    }
  }, [getPaymentDetailsResult]);

  const updatePaymentDetails = useCallback(
    async (body: PatchPaymentDetailsBody) => {
      const patchResult = await patchPaymentDetails(body);

      if (!patchResult.error) {
        setPaymentDetails(patchResult.result);
      }

      return patchResult;
    },
    [patchPaymentDetails]
  );

  const cancelPayment = useCallback(async () => {
    const postResult = await postCancelPayment();

    if (!postResult.error) {
      setPaymentDetails(postResult.result);
    }

    return postResult;
  }, [postCancelPayment]);

  const error = getError || patchError || pollingError;
  const isLoading = isGetLoading;
  const isFetching = isGetLoading || isPatchFetching;
  const isError = isGetError || isPatchError || isPollingError;
  const isLoadingError = isGetError;
  const loadingError = getError;

  return (
    <ScaPaymentDetailsContext.Provider
      value={{
        paymentDetails,
        updatePaymentDetails,
        error,
        isLoading,
        isLoadingError,
        loadingError,
        isFetching,
        isError,
        refetchPaymentDetails,
        cancelPayment,
        isCancelFetching,
        isCancelError,
        cancelError,
      }}
    >
      {children}
    </ScaPaymentDetailsContext.Provider>
  );
};

export const withScaPaymentDetailsContextProvider =
  (props: Omit<GlobalApiPaymentDetailsContextProviderProps, 'children'>) =>
  ({ children }: PropsWithChildren) => (
    <ScaPaymentDetailsContextProvider {...props}>
      {children}
    </ScaPaymentDetailsContextProvider>
  );

export const withScaPaymentDetailsContextDecorator = (
  data?: Partial<ScaApiPaymentDetailsContextData>
) => ({
  context: ScaPaymentDetailsContext as Context<ScaApiPaymentDetailsContextData>,
  contextValue: ({
    args,
  }: {
    args: Partial<ScaApiPaymentDetailsContextData>;
  }) => ({
    ...SCA_PAYMENT_DETAILS_CONTEXT_DEFAULT,
    ...data,
    ...args,
  }),
});
